import { Injectable, inject } from '@angular/core';
import { Order } from '../model/order';
import { GenesisUnitOfWork } from './genesis-unit-of-work';
import _ from 'lodash';
import { Confirmation, DealerTerm, Note, OrderPaymentMethod } from '../model/entity-model';
import { AuthService } from '../auth.service';
import { AppInformationService } from '../app-information.service';
import { alert, confirm, custom } from 'devextreme/ui/dialog';
import { Router } from '@angular/router';

@Injectable()
export class OrderService {
    private uow = inject(GenesisUnitOfWork);
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    router = inject(Router);

    clearShipping(order: Order) {
        (order as any).shipCompanyName = null;
        order.shipToContactName = null;
        order.shipToLocation = null;
        order.shipToAddress1 = null;
        order.shipToAddress2 = null;
        order.shipToCity = null;
        order.shipToState = null;
        order.shipToZip = null;
        order.shipToCountry = null;
        order.shipToPhone = null;
        order.shipToEmail = null;
        order.shippingNote = null;
    }

    copyBilling(order: Order) {
        this.clearShipping(order);
        (order as any).shipCompanyName = order.dealer.companyName;
        order.shipToContactName = order.dealer.contactName;
        order.shipToLocation = null;
        order.shipToAddress1 = order.dealer.billAddress1;
        order.shipToAddress2 = order.dealer.billAddress2;
        order.shipToCity = order.dealer.billCity;
        order.shipToState = order.dealer.billState;
        order.shipToZip = order.dealer.billZip;
        order.shipToCountry = order.dealer.billCountry;
        order.shipToPhone = order.dealer.phone1;
        order.shipToEmail = null;
        if (!order.shippingNote || order.shippingNote.trim() === '') {
            order.shippingNote = order.dealer.shippingNote;
        }
    }

    async getAttachments(orderId: number) {
        if (!orderId) return [];

        const attachments = await this.uow.getOrderAttachments(orderId);
        const photos = await this.uow.getPhotos(orderId, 'Order');
        return _([...attachments, ...photos])
            .orderBy('name')
            .valueOf();
    }

    getPaymentMethodId(dealerTermId?: number) {
        switch (dealerTermId) {
            case DealerTerm.Terms.COD:
                return OrderPaymentMethod.Methods.COD;
            case DealerTerm.Terms.NET30:
                return OrderPaymentMethod.Methods.NET30;
            case DealerTerm.Terms.NorthpointFinancial:
                return OrderPaymentMethod.Methods.Northpoint;
            default:
                return null;
        }
    }

    deleteConfirmations(confirmations: Confirmation[], deleteReason?: string) {
        confirmations
            .filter((confirmation) => this.canDeleteConfirmation(confirmation))
            .forEach((confirmation) => {
                confirmation.deleted = new Date();
                if (deleteReason) confirmation.notes = `Confirmation deleted because ${deleteReason}`;
                confirmation.aches?.forEach((ach) => {
                    ach.deleted = new Date();
                    ach.deletedBy = this.auth.currentUser.userName;
                    if (deleteReason) ach.notes = `Ach deleted because ${deleteReason}`;
                });
            });
    }

    canDeleteConfirmation(confirmation: Confirmation) {
        if (confirmation.salesRepConfirmation) return true;

        const isOpen = this.isConfirmationOpen(confirmation);
        const hasClosedAches = this.confirmationHasClosedAches(confirmation);
        return isOpen && !hasClosedAches;
    }

    isConfirmationOpen(confirmation: Confirmation) {
        return !confirmation.approved && !confirmation.declined;
    }

    confirmationHasClosedAches(confirmation: Confirmation) {
        return confirmation.aches?.length > 0 && confirmation.aches.some((ach) => !ach.deleted && (ach.approved || ach.declined || ach.manualFormSelected));
    }

    addOrderNote(order: Order, note: string) {
        const newNote = this.uow.notes.createEntity({
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
            notes: note,
            entityTypeName: 'Order',
            primaryKey: order.id,
        }) as Note;
    }
}
